export type NotificationSeverity = "success" | "warning" | "error" | "info";

export interface NotificationOptions {
  message: string;
  severity: NotificationSeverity;
  duration?: number; // in milliseconds, default 5000
}

class NotificationServiceClass {
  private manager?: HTMLElement;

  /**
   * Initialize the notification service with a manager element
   * This should be called once during app startup
   */
  initialize(manager: HTMLElement) {
    if (this.manager && this.manager !== manager) {
      console.warn("NotificationService: Multiple managers detected. Using the first one.");
      return;
    }
    this.manager = manager;
  }

  /**
   * Show a notification
   */
  show(options: NotificationOptions) {
    if (!this.manager) {
      console.warn("NotificationService: Manager not initialized. Call initialize() first.");
      return;
    }

    // Dispatch custom event to the manager
    this.manager.dispatchEvent(
      new CustomEvent("show-notification", {
        detail: options,
      }),
    );
  }

  /**
   * Convenience methods for different severity levels
   */
  success(message: string, duration?: number) {
    this.show({ message, severity: "success", duration });
  }

  warning(message: string, duration?: number) {
    this.show({ message, severity: "warning", duration });
  }

  error(message: string, duration?: number) {
    this.show({ message, severity: "error", duration });
  }

  info(message: string, duration?: number) {
    this.show({ message, severity: "info", duration });
  }
}

// Export singleton instance
export const NotificationService = new NotificationServiceClass();
